import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Grid,
  Paper,
} from '@mui/material';
import { CheckCircle, RadioButtonUnchecked, VideoLibrary } from '@mui/icons-material';
import { Lecon } from '../../types';
import { formatDate } from '../../utils/dates';
import LeconDureeVideo from './LeconDureeVideo';

interface LeconDetailProps {
  lecon: Lecon;
}

const LeconDetail: React.FC<LeconDetailProps> = ({ lecon }) => {
  return (
    <Box>
      {/* En-tête */}
      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          {lecon.titre}
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
          <Chip
            label={lecon.niveauDifficulte}
            color={
              lecon.niveauDifficulte === 'facile'
                ? 'success'
                : lecon.niveauDifficulte === 'moyen'
                ? 'warning'
                : 'error'
            }
            size="small"
          />
          <Chip
            label={(lecon.statut || 'published') === 'draft' ? '🔸 Brouillon' : '✅ Publié'}
            color={(lecon.statut || 'published') === 'draft' ? 'warning' : 'success'}
            size="small"
          />
          <Chip
            label={lecon.estActif ? 'Actif' : 'Inactif'}
            color={lecon.estActif ? 'success' : 'default'}
            size="small"
            icon={lecon.estActif ? <CheckCircle /> : <RadioButtonUnchecked />}
          />
          <Chip
            label={`Ordre: ${lecon.ordre}`}
            color="default"
            size="small"
            variant="outlined"
          />
          {lecon.tempsEstimeMinutes && (
            <Chip
              label={`${lecon.tempsEstimeMinutes} min`}
              color="info"
              size="small"
              variant="outlined"
            />
          )}
          {lecon.videoId && (
            <Chip
              icon={<VideoLibrary />}
              label="Vidéo disponible"
              color="secondary"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Informations générales */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom color="primary">
              Informations générales
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.5}>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {lecon.id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Chapitre
                </Typography>
                <Typography variant="body2">{lecon.chapitreId}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Ordre
                </Typography>
                <Typography variant="body2">{lecon.ordre}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Niveau de difficulté
                </Typography>
                <Chip
                  label={lecon.niveauDifficulte}
                  color={
                    lecon.niveauDifficulte === 'facile'
                      ? 'success'
                      : lecon.niveauDifficulte === 'moyen'
                      ? 'warning'
                      : 'error'
                  }
                  size="small"
                />
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Durée estimée
                </Typography>
                <Typography variant="body2">
                  {lecon.tempsEstimeMinutes} minutes
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom color="primary">
              Dates
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.5}>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Date de création
                </Typography>
                <Typography variant="body2">
                  {formatDate(lecon.dateCreation, 'dayMonthLong')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="textSecondary">
                  Dernière modification
                </Typography>
                <Typography variant="body2">
                  {formatDate(lecon.dateModification, 'dayMonthLong')}
                </Typography>
              </Box>
              {lecon.datePublication && (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Date de publication
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(lecon.datePublication, 'dayMonthLong')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Historique des statuts */}
      {lecon.historiqueStatut && lecon.historiqueStatut.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Historique des statuts
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            {lecon.historiqueStatut.map((entry, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  borderLeft: 4,
                  borderColor: entry.statut === 'published' ? 'success.main' : 'warning.main'
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <Chip
                    label={entry.statut === 'draft' ? '🔸 Brouillon' : '✅ Publié'}
                    color={entry.statut === 'draft' ? 'warning' : 'success'}
                    size="small"
                  />
                  <Typography variant="body2" color="textSecondary">
                    {formatDate(entry.date, 'dateTimeLong')}
                  </Typography>
                </Box>
                {entry.utilisateur && (
                  <Typography variant="body2" color="textSecondary">
                    Par: {entry.utilisateur}
                  </Typography>
                )}
                {entry.commentaire && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {entry.commentaire}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Description */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom color="primary">
          Description
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {lecon.description}
        </Typography>
      </Paper>

      {/* Mots-clés */}
      {lecon.motsCles && lecon.motsCles.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Mots-clés
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {lecon.motsCles.map((motCle, index) => (
              <Chip
                key={index}
                label={motCle}
                variant="outlined"
                color="primary"
                size="small"
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Vidéo */}
      {lecon.videoId && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Vidéo associée
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <VideoLibrary color="secondary" />
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {lecon.videoId}
            </Typography>
          </Box>
          <LeconDureeVideo lecon={lecon} />
        </Paper>
      )}

      {/* Thumbnail LaTeX */}
      {lecon.thumbnailLatex && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Thumbnail LaTeX
          </Typography>
          <Paper
            sx={{
              p: 1.5,
              mt: 1,
              bgcolor: 'grey.100',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              overflow: 'auto',
            }}
          >
            <pre style={{ margin: 0 }}>{lecon.thumbnailLatex}</pre>
          </Paper>
        </Paper>
      )}

      {/* Contenu LaTeX */}
      {lecon.contenuLatex && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Contenu LaTeX
          </Typography>
          <Paper
            sx={{
              p: 2,
              mt: 1,
              bgcolor: 'grey.100',
              fontFamily: 'monospace',
              fontSize: '0.85rem',
              overflow: 'auto',
              maxHeight: 400,
            }}
          >
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
              {lecon.contenuLatex}
            </pre>
          </Paper>
        </Paper>
      )}
    </Box>
  );
};

export default LeconDetail;
